import React from 'react';
import I18n from 'i18n-js';
import track from 'react-tracking';

import ListingCard from '@reverbdotcom/commons/src/components/listing_card';
import { LISTING_TYPE } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { Listing } from '../../schemas/rql';

import ErrorDisplay from '../discovery/error_display';
import { PageSettings } from './map_params';
import NotFoundAnimation from '../not_found_animation';
import TileCurationView from '../discovery/curation/tile_curation_view';
import { withUserContext, IUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import ListViewRowCard from '../list_view_row_card';
import { RCListingGrid } from '@reverbdotcom/cadence/components/RCListingGrid/RCListingGrid';
import { times } from 'lodash';
import { PlaceholderListingRowCard } from '@reverbdotcom/commons/src/components/listing_row_card';
import { RCListingCard } from '@reverbdotcom/cadence/components';
import { Filter } from '@reverbdotcom/commons/src/components/search_filters';
import MarketplaceGridWithSidebar from './marketplace_grid_with_sidebar';

interface ExternalProps {
  loading: boolean;
  error: boolean;
  listings: Listing[];
  showingSold: boolean;
  trackingName?: string;
  emptyGrid?: JSX.Element;
  trackingQuery?: string;
  trackingFilter?: string;
  trackingSort?: string;
  trackingPage?: number;
  pageSettings?: PageSettings;
  isListView?: boolean;
  useExposedSidebar?: boolean;
  exposedSidebarOpen?: boolean;
  filters?: Filter[];
  clearFiltersDisabled?: boolean;
  toggleExposedSidebarOpen?: () => void;
  listingSearchTotal?: number;
  clearFilters?:()=>void;
}

type IProps = ExternalProps & IUserContext;

export const BRAND_NEW_CONDITION_UUID = '7c3f45de-2ae0-4c81-8400-fdb6b1d74890';

const trackingContext = {
  componentName: 'GridView',
  type: LISTING_TYPE,
};

export function GridEmptyState() {
  return (
    <div className="not-found">
      <h3 className="not-found__message">
        {I18n.t('discovery.noResults.nothingHere')}
      </h3>
      <NotFoundAnimation />
    </div>
  );
}

export function ListingGrid(props: IProps) {
  if (props.error) {
    return <ErrorDisplay />;
  }

  function renderCards(listing: Listing, idx: number) {
    if (props.isListView) {
      return (
        <ListViewRowCard
          key={listing.id}
          listing={listing}
          loading={props.loading}
          position={idx}
          trackingQuery={props.trackingQuery}
          showingSold={props.showingSold}
        />
      );
    }

    return (
      <ListingCard
        key={listing.id}
        listing={listing}
        position={idx}
        soldListing={props.showingSold}
        trackingName={props.trackingName}
        trackingQuery={props.trackingQuery}
        trackingFilter={props.trackingFilter}
        trackingSort={props.trackingSort}
        trackingPage={props.trackingPage}
        footerContent={
          props.user?.isAdmin &&
          <TileCurationView
            listingId={listing.id}
            pageSettings={props.pageSettings}
            bumped={listing.bumped}
          />
        }
      />
    );
  }

  function renderEmptyGridState() {
    return (
      <RCListingGrid.Item colSpan="all">
        {props.emptyGrid || <GridEmptyState />}
      </RCListingGrid.Item>
    );
  }

  function renderGridItems() {
    if (!props.loading && !props.listings?.length) {
      return renderEmptyGridState();
    }

    if (props.loading && !props.listings?.length) {
      return (
        <>
          {times(8, index => (
            <RCListingGrid.Item key={index} colSpan={props.isListView ? 'all' : 'one'}>
              {props.isListView
                ? <PlaceholderListingRowCard />
                : <RCListingCard.Skeleton />
              }
            </RCListingGrid.Item>
          ))}
        </>
      );
    }

    return props.listings.map((listing, idx) => {
      return (
        <RCListingGrid.Item key={listing.id} colSpan={props.isListView ? 'all' : 'one'}>
          {renderCards(listing, idx)}
        </RCListingGrid.Item>
      );
    });
  }

  return (
    <MarketplaceGridWithSidebar
      loading={props.loading}
      filters={props.filters}
      exposedSidebarOpen={props.exposedSidebarOpen}
      useExposedSidebar={props.useExposedSidebar}
      closeSidebarFilters={props.toggleExposedSidebarOpen}
      listingSearchTotal={props.listingSearchTotal}
      clearFiltersDisabled= {props.clearFiltersDisabled}
      clearFilters={props.clearFilters}
    >
      {renderGridItems()}
    </MarketplaceGridWithSidebar>
  );
}

export default withUserContext<IProps>(track(trackingContext)(ListingGrid));
